import { useState, useCallback, useEffect } from 'react';
import { QuotationDestinationList, QuotationRegion } from '../../../types';
import { getRegions } from '../../../services';

interface UseDestinationSelectionReturn {
	regions: QuotationRegion[];
	selectedRegions: string[];
	selectedDestinations: QuotationDestinationList[];
	loading: boolean;
	error: string | null;
	handleRegionClick: (regionName: string) => void;
	handleDestinationsChange: (destinations: QuotationDestinationList[]) => void;
	clearDestinations: () => void;
	getDestinationCodes: () => string[];
}

export const useDestinationSelection = (): UseDestinationSelectionReturn => {
	const [regions, setRegions] = useState<QuotationRegion[]>([]);
	const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
	const [selectedDestinations, setSelectedDestinations] = useState<QuotationDestinationList[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const fetchRegions = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);
			const result = await getRegions();

			// Agregar región especial para grupos
			const newRegions = [
				...result.data,
				{
					name: 'regionGroupSelect',
					destinations: result.data.map((x) => ({
						code: x.name,
						name: x.name,
						regionName: 'regionGroupSelect',
					})),
				},
			];

			setRegions(newRegions);
		} catch (err) {
			setError('Error cargando regiones');
			console.error('Error fetching regions:', err);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchRegions();
	}, [fetchRegions]);

	const handleRegionClick = useCallback((regionName: string) => {
		setSelectedRegions((prev) => {
			const indexOf = prev.indexOf(regionName);

			if (indexOf === -1) {
				// Agregar región
				setSelectedDestinations((prevDest) => [
					...prevDest,
					{ code: regionName, name: regionName, regionName: 'regionGroupSelect' },
				]);
				return [...prev, regionName];
			} else {
				// Remover región
				setSelectedDestinations((prevDest) =>
					prevDest.filter((dest) => !(dest.code === regionName && dest.regionName === 'regionGroupSelect')),
				);
				return prev.filter((region) => region !== regionName);
			}
		});
	}, []);

	const handleDestinationsChange = useCallback((destinations: QuotationDestinationList[]) => {
		setSelectedDestinations(destinations);

		// Actualizar regiones seleccionadas basado en destinos
		setSelectedRegions((prev) =>
			prev.filter((regionName) =>
				destinations.some((dest) => dest.code === regionName && dest.regionName === 'regionGroupSelect'),
			),
		);
	}, []);

	const clearDestinations = useCallback(() => {
		setSelectedDestinations([]);
		setSelectedRegions([]);
	}, []);

	const getDestinationCodes = useCallback((): string[] => {
		const destinationCodes: string[] = [];

		selectedDestinations.forEach((dest) => {
			if (dest.regionName === 'regionGroupSelect') {
				// Es una región completa
				const region = regions.find((r) => r.name === dest.code);
				if (region) {
					region.destinations.forEach((regionDest) => {
						destinationCodes.push(regionDest.code);
					});
				}
			} else {
				// Es un destino individual
				destinationCodes.push(dest.code);
			}
		});

		return destinationCodes;
	}, [selectedDestinations, regions]);

	return {
		regions,
		selectedRegions,
		selectedDestinations,
		loading,
		error,
		handleRegionClick,
		handleDestinationsChange,
		clearDestinations,
		getDestinationCodes,
	};
};
